import { DeleteOutlined, EditOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Modal, Space, Table, Tooltip, message, Spin } from "antd";
import axios from "axios";
import { PRIMARY_RED } from "constants/colors";
import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Row } from "reactstrap";
import { setBreadcrumbItems } from "../../store/actions";

const Employees = (props) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employee, setEmployee] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = authUser?.token;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };


  const breadcrumbItems = [
    { title: "Employee", link: "#" },
    { title: "Employee Listing", link: "#" },
  ];

  useEffect(() => {
    props.setBreadcrumbItems("Employee", breadcrumbItems);
    if (!token) {
      message.error("Session expired, please login again");
    } else {
      fetchEmployee();
    }
  }, [token]);

  useEffect(() => {
    // Reset to first page on search
    setCurrentPage(1);
  }, [searchText]);

  const fetchEmployee = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASEURL}/employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployee(response.data);
    } catch (err) {
      message.error("Failed to load employees");
    }
  };

  const handleDelete = async (userId, employeeName) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASEURL}/employees/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success(`Deleted employee: ${employeeName}`);
      fetchEmployee();
    } catch (err) {
      message.error("Failed to delete employee");
    }
  };

  const [importConfirmVisible, setImportConfirmVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setImportConfirmVisible(true);
    // Reset input value so same file can be selected again if needed
    event.target.value = null;
  };

  const handleDiscardUpload = () => {
    setSelectedFile(null);
    setImportConfirmVisible(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const processUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      message.loading({ content: 'Importing...', key: 'importMsg' });

      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${process.env.REACT_APP_API_BASEURL}/employees/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      const { processed, skipped, errors, skippedDetails } = response.data.stats || { processed: 0, skipped: 0, errors: [] };

      if (errors.length > 0) console.warn('Import Errors:', errors);
      if (skippedDetails && skippedDetails.length > 0) console.warn('Skipped Records:', skippedDetails);

      message.success({
        content: `Import complete: ${processed} added, ${skipped} skipped, ${errors.length} failed.`,
        key: 'importMsg',
        duration: 5
      });
      fetchEmployee();
      handleDiscardUpload(); // Close modal and clear file on success
    } catch (error) {
      console.error(error);
      const errorMsg = error.response?.data?.message || 'Failed to import employees';
      message.error({ content: errorMsg, key: 'importMsg', duration: 5 });
      // Keep modal open on error so user can retry or cancel, or close it? 
      // User requirement says "Discard should clear", proceeding is standard.
      // We'll close it to match standard success flow, or keep?
      // Let's close it to be safe or maybe user wants to try another file.
      setImportConfirmVisible(false);
      setSelectedFile(null);
    } finally {
      setUploading(false);
    }
  };

  const filteredEmployees = employee.filter((emp) => {
    const search = searchText.toLowerCase();
    return (
      (emp.employeeName || "").toLowerCase().includes(search) ||
      (emp.employeeCode || "").toLowerCase().includes(search) ||
      (emp.email || "").toLowerCase().includes(search) ||
      String(emp.contactNumber || "").toLowerCase().includes(search) ||
      (emp.designation?.designation || "").toLowerCase().includes(search)
    );
  });

  const paginatedData = filteredEmployees.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );


  const columns = [
    { title: "Employee Name", dataIndex: "employeeName", key: "employeeName" },
    { title: "Employee Code", dataIndex: "employeeCode", key: "employeeCode" },
    {
      title: "Designation",
      key: "designation",
      render: (_, record) => record.designation?.designation || "N/A"
    },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Contact Number", dataIndex: "contactNumber", key: "contactNumber" },

    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(`/edit-employee/${record._id}`)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Tooltip title="Delete">

              <Button
                type="text"
                icon={<DeleteOutlined />}
                style={{ color: PRIMARY_RED }}
                onClick={() => {
                  setSelectedEmployee(record);
                  setIsModalVisible(true);
                }}
              />
            </Tooltip>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <React.Fragment>
      <Row>
        <Col xs="30">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <input
              type="text"
              placeholder="Search employees..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250, padding: "6px 10px" }}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="file"
                accept=".xlsx, .xls"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileUpload}
              />
              <Button
                icon={<UploadOutlined />}
                loading={uploading}
                onClick={() => fileInputRef.current.click()}
              >
                Bulk Import
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ backgroundColor: PRIMARY_RED }}
                onClick={() => navigate("/add-employee")}
              >
                Add Employee
              </Button>
            </div>
          </div>
          <Card>
            <Modal
              title="Delete Employee"
              visible={isModalVisible}
              onCancel={() => setIsModalVisible(false)}
              footer={[
                <Button key="cancel" onClick={() => setIsModalVisible(false)} style={{ backgroundColor: "#6c757d", color: "white" }}>
                  Close
                </Button>,
                <Button
                  key="delete"
                  type="primary"
                  style={{ backgroundColor: PRIMARY_RED }}
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    handleDelete(selectedEmployee._id, selectedEmployee.employeeName);
                    setIsModalVisible(false);
                  }}
                >
                  Delete
                </Button>,
              ]}
            >
              <p>Are you sure you want to delete <strong>{selectedEmployee?.employeeName}</strong> employee?</p>
            </Modal>

            {/* Bulk Import Confirmation Modal */}
            <Modal
              title="Confirm Bulk Import"
              visible={importConfirmVisible}
              onCancel={handleDiscardUpload}
              footer={[
                <Button key="discard" onClick={handleDiscardUpload} style={{ backgroundColor: "#6c757d", color: "white" }}>
                  Discard
                </Button>,
                <Button
                  key="proceed"
                  type="primary"
                  style={{ backgroundColor: PRIMARY_RED }}
                  loading={uploading}
                  onClick={processUpload}
                >
                  Proceed
                </Button>,
              ]}
            >
              <p>Are you sure you want to import this file?</p>
              <p><strong>File:</strong> {selectedFile?.name}</p>
            </Modal>
            <CardBody>
              <Table
                columns={columns}
                dataSource={paginatedData}
                rowKey="_id"
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: filteredEmployees.length,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50"],
                }}
                onChange={handleTableChange}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default connect(null, { setBreadcrumbItems })(Employees);
